import { HUD } from '../../config/tuning';
import type { HudPainter } from '../HudLayout';
import { RwrState, type HudFrame } from '../HudFrame';

/**
 * The radar warning receiver (SPEC §3.4.1): a 120 px ring at bottom centre showing the bearing
 * and class of every emitting radar.
 *
 * **Aircraft-relative, and it rotates with heading rather than with bank.** That distinction is
 * the whole reason the instrument works: a contact at the top of the ring is off the nose, and
 * stays off the nose while the player rolls. Tying it to bank would make the display spin during
 * exactly the manoeuvre a player performs *because* of what the RWR is telling them.
 *
 * Three states, three glyphs: SEARCH is a hollow tick outside the ring, TRACK is a solid tick
 * that crosses it, LAUNCH flashes. Being able to tell them apart at a glance is what the notch
 * mechanic in the late campaign is built on.
 */
export function drawRwr(painter: HudPainter, frame: HudFrame): void {
  const layout = painter.layout;
  const ctx = painter.ctx;

  const radius = layout.px(HUD.rwrDiameterPx) * 0.5;
  const centreX = layout.centreX;
  const centreY = layout.heightPx - layout.px(HUD.rwrBottomMarginPx) - radius;
  const tick = layout.px(HUD.rwrTickPx);

  ctx.save();
  ctx.translate(centreX, centreY);

  painter.circle(0, 0, radius, 0.8);

  // Nose reference: a small notch at the top of the ring.
  painter.line(0, -radius - tick * 0.5, 0, -radius + tick * 0.5, 0.8);

  // Cardinal ticks at the beam and tail positions, so bearing is readable without counting.
  for (const angle of CARDINALS) {
    const sin = Math.sin(angle);
    const cos = Math.cos(angle);
    painter.line(
      sin * (radius - tick * 0.4),
      -cos * (radius - tick * 0.4),
      sin * radius,
      -cos * radius,
      0.7,
    );
  }

  const flashOn =
    Math.floor(frame.simTimeS * HUD.rwrLaunchFlashHz) % 2 === 0;

  for (let i = 0; i < frame.rwrContacts.length; i += 1) {
    const contact = frame.rwrContacts[i];
    const sin = Math.sin(contact.relativeBearingRad);
    const cos = Math.cos(contact.relativeBearingRad);

    if (contact.state === RwrState.Launch && !flashOn) continue;

    const inner = contact.state === RwrState.Search ? radius : radius - tick;
    const outer = contact.state === RwrState.Search ? radius + tick : radius + tick * 0.6;
    const width = contact.state === RwrState.Search ? 1 : 2.2;
    const colour = contact.state === RwrState.Launch ? HUD.dangerColour : undefined;

    painter.line(sin * inner, -cos * inner, sin * outer, -cos * outer, width, colour);

    const labelRadius = outer + layout.fontPx * 0.9;
    painter.text(contact.label, sin * labelRadius, -cos * labelRadius, 'centre', colour);
  }

  ctx.restore();
}

const CARDINALS = [Math.PI * 0.5, Math.PI, Math.PI * 1.5];
